export function isString(val: unknown): val is string {
  return typeof val === 'string';
}

export function isBoolean(val: unknown): val is boolean {
  return typeof val === 'boolean';
}

export function isNumber(val: unknown): val is number {
  return typeof val === 'number';
}

export function isFunction<Func extends (...args: never) => unknown>(val: unknown): val is Func;
export function isFunction<Args extends unknown[] = unknown[], Return = unknown>(val: unknown): val is (...args: Args) => Return;

export function isFunction<ArgsOrFunction extends (...args: never) => unknown | unknown[] = () => void, Return = unknown>(val: unknown):
  val is ArgsOrFunction extends unknown[]
    ? (...args: ArgsOrFunction) => Return
    : ArgsOrFunction {
  return typeof val === 'function';
}

export function isUndefined(val: unknown): val is undefined {
  return val === undefined;
}

export function isDefined<T>(val: T | undefined | null): val is T {
  return val !== undefined && val !== null;
}

export function isType<T>(val: unknown, expected: T): val is T;
export function isType<T>(val: unknown, condition: (val: unknown) => boolean): val is T;
export function isType<T>(val: unknown, matchesType: boolean): val is T;

/**
 * Tests if the given value matches the condition or boolean, and indicates to TypeScript it is one.
 *
 * Use isInstanceOf if you want to check if it is an instance of a type.
 * Use isString, isBoolean, or isNumber for checking booleans.
 * @param val
 * @param conditionOrExpectedOrBoolean
 * @example
 * isType<>
 */
export function isType<T extends object>(val: unknown, conditionOrExpectedOrBoolean: T | boolean | ((val: unknown) => boolean) | { new(): T }): val is T {
  if (isFunction(conditionOrExpectedOrBoolean)) {
    if (conditionOrExpectedOrBoolean.prototype && val instanceof conditionOrExpectedOrBoolean) {
      return true;
    }

    try {
      return conditionOrExpectedOrBoolean(val);
    } catch {
      return false;
    }
  }

  if (isBoolean(conditionOrExpectedOrBoolean)) {
    return conditionOrExpectedOrBoolean;
  }

  return val === conditionOrExpectedOrBoolean;
}

export function isKeyOf<T>(key: PropertyKey, data: T): key is keyof T {
  return Object.hasOwnProperty.call(data, key);
}